import { AnalyticsBrowser } from '@segment/analytics-next';
import { posthog } from 'posthog-js';
import { useCallback, useEffect, useRef, useState } from 'react';
import Logger from '@/app/shared/utils/logger';
import { convertKeysToSnakeCase } from '@/app/shared/utils/stringFormat';
import { useAppSelector } from '@/store/hooks';
import { selectReferences, selectUploadedDocsCount } from '@/store/reducers/uploadSlice';
import { PAGE_NAME } from '../constants/segmentConstants';
import { useAnalyticsContext } from '../context/analyticsContext';

const dateTimeNow = new Date().toISOString();
const analytics = AnalyticsBrowser.load({ writeKey: import.meta.env.REACT_APP_SEGMENT_KEY as string });
const logger = new Logger('iLendingHubSegmentAnalytics');

export type NestedObject = {
  [key: string]: string | number | boolean | null | undefined | NestedObject | NestedObject[];
};

analytics.ready().then(() => {
  posthog.init(import.meta.env.REACT_APP_POSTHOG_KEY as string, {
    api_host: 'https://us.i.posthog.com',
    capture_pageview: false,
    loaded: () => {
      logger.log('PostHog loaded and connected to Segment');
    },
  });
});

const getEntryPath = (source?: string): string => {
  if (!source) return 'direct_link';

  const normalizedSource = source.toLowerCase();

  const sourceMap: Record<string, string> = {
    cdx: 'cdx_redirect',
    thankyoupage: 'typ-redirect',
    initialcommunication: 'initial_communication_redirect',
  };

  return sourceMap[normalizedSource] || 'direct_link';
};

export const useAnalytics = (screenParams = {}, isTrackAbandon = true) => {
  const {dispatch}= useAnalyticsContext();
  const references = useAppSelector(selectReferences);
  const uploadedDocsCount = useAppSelector(selectUploadedDocsCount);
    const actualReferences = Object.values(references).filter(
      (ref) => Object.keys(ref).length > 0
    );

  type TimeoutId = ReturnType<typeof setTimeout> | null;

  // eslint-disable-next-line react-hooks/purity
  const renderTimeRef = useRef(Date.now());
  const interactionStartedRef = useRef(false);
  const interactionStartTimeRef = useRef<number | null>(null);
  const idleTimerRef = useRef<TimeoutId>(null);
  const abandonedRef = useRef(false);
  const pageExitTrackedRef = useRef(false);
  const lastActionRef = useRef<string | null>(null);
  const [isIdle, setIsIdle] = useState(false);

  const IDLE_TIMEOUT = 5 * 60 * 1000; // 5 minutes

  const { source } = useAppSelector((state) => state.auth);
  const params = new URLSearchParams(location.search);
  const contactIdParam = params.get('contactId') || '';
  const paramSource = params.get('source') || undefined;
  const effectiveSource = source || paramSource;
  const entryPath = getEntryPath(effectiveSource);

  const extraParameters = {
    session_id: params.get('session_id') || undefined,
    properties: {
      ...screenParams,
      page_name: PAGE_NAME,
      entry_path: entryPath,
      referrer: params.get('referrer') || undefined,
      utm_source: params.get('utm_source') || undefined,
      utm_medium: params.get('utm_medium') || undefined,
      utm_campaign: params.get('anonymous_id') || undefined,
      utm_term: params.get('utm_term') || undefined,
      utm_content: params.get('utm_content') || undefined,
    },
    sentAt: dateTimeNow,
  };

  const identifierObject = contactIdParam
    ? {
        userId: contactIdParam,
      }
    : {
        anonymousId: params.get('anonymous_id') || '',
      };

 
  //Calculate time spent on form (only after interaction has started)
  const getTimeSpentMs = useCallback(() => {
    if (!interactionStartTimeRef.current) {
      return 0;
    }
    return Date.now() - interactionStartTimeRef.current;
  }, []);

  //Send abandon event with time tracking (only if interaction started)
  const trackAbandonEvent = useCallback(
    (abandonReason: string, additionalParams?: any) => {
      if (!interactionStartedRef.current) {
        logger.log('Abandon event not tracked - interaction never started');
        return;
      }

      const timeSpentMs = getTimeSpentMs();
      const abandonParameters = {
        ...extraParameters,
        ...additionalParams,
        abandon_reason: abandonReason,
        time_on_page_seconds: timeSpentMs,
      };
      if (Object.keys(screenParams).length > 0 && isTrackAbandon) {
        try {
          const parametersWithUtm = {
            ...abandonParameters,
            ...extraParameters,
            ...screenParams,
            last_action: lastActionRef.current || 'unknown',
            documents_uploaded: uploadedDocsCount,
            contacts_added: actualReferences.length,
          };
          analytics.track('iLending Hub Abandoned', parametersWithUtm,  {
            transport: 'beacon'   // uses navigator.sendBeacon() under the hood
          });
          logger.log('iLending Hub Abandoned Track ->', {
            reason: abandonReason,
            timeSpentMs,
          });
        } catch (error) {
          logger.log('iLending Hub Abandoned Track error ->', abandonReason, error);
        }
      }
    },
    [extraParameters, getTimeSpentMs, screenParams, isTrackAbandon, uploadedDocsCount, actualReferences]
  );

  // Reset idle timer on user activity
  const resetIdleTimer = useCallback(() => {
    if (!interactionStartedRef.current) {
      return;
    }

    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    setIsIdle(false);

    idleTimerRef.current = setTimeout(() => {
      if (!abandonedRef.current) {
        abandonedRef.current = true;
        trackAbandonEvent('idle_timeout', { idle_duration_ms: IDLE_TIMEOUT });
        setIsIdle(true);
      }
    }, IDLE_TIMEOUT);
  }, [trackAbandonEvent]);

  const startInteraction = useCallback((firstAction: string) => {
    if (!interactionStartedRef.current) {
      interactionStartedRef.current = true;
      interactionStartTimeRef.current = Date.now();
      if (Object.keys(screenParams).length > 0) {
        try {
          const parametersWithUtm = {
            ...extraParameters,
            ...screenParams,
            first_action: firstAction
          };
          analytics.track('iLending Hub Interaction', parametersWithUtm, identifierObject);
          logger.log('iLending Hub Interaction started ->', {
            firstAction,
          });
        } catch (error) {
          logger.log('iLending Hub Interaction started error', error);
        }
      }
      resetIdleTimer();
    }
  }, [extraParameters, getTimeSpentMs, resetIdleTimer, screenParams]);

 // Initialize time tracking and listeners
  useEffect(() => {
    renderTimeRef.current = Date.now();
    abandonedRef.current = false;
    pageExitTrackedRef.current = false;

    // Listen for user activity to reset idle timer (only if interaction started)
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click', 'input', 'change'];
    const handleActivity = () => {
      if (interactionStartedRef.current) {
        resetIdleTimer();
      }
    };

    activityEvents.forEach((event) => {
      window.addEventListener(event, handleActivity);
    });

    // Listen for tab/window close (not page refresh)
    const handleBeforeUnload = () => {
      if (pageExitTrackedRef.current) {
        return;
      }

      if (!abandonedRef.current && interactionStartedRef.current) {
        // Check if it's a page refresh or actual tab close
        const perfData = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
        const isRefresh = perfData?.type === 'reload';

        // Only track tab close, not refresh
        if (!isRefresh) {
          pageExitTrackedRef.current = true;
          abandonedRef.current = true;
          trackAbandonEvent('page_exit');
        }
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }
    };
  }, [resetIdleTimer, trackAbandonEvent]);

  const trackPage = useCallback(
    (pageTitle: string, parameters?: any) => {
      const parametersWithUtm = {
        ...parameters,
        ...extraParameters,
      };
      lastActionRef.current = pageTitle;
      try {
        analytics.track('Page Viewed', parametersWithUtm, identifierObject);
        logger.log('Analytics trackPage ->', pageTitle);
      } catch (error) {
        logger.log('Analytics trackPage error ->', pageTitle, error);
      }
      dispatch({ type: 'TRACK_PAGE', payload: { eventType: pageTitle, parameters: parametersWithUtm } });
    },
    [dispatch, extraParameters, identifierObject]
  );

  const trackClick = useCallback(
    (eventType: string, parameters?: any) => {
      const formatParameters = convertKeysToSnakeCase(parameters);
      const parametersWithUtm = {
        ...formatParameters,
        ...extraParameters,
      };
      lastActionRef.current = eventType;
      try {
        analytics.track(eventType, parametersWithUtm, identifierObject);
        logger.log('Analytics trackClick ->', eventType, parametersWithUtm ?? '');
      } catch (error) {
        logger.log('Analytics trackClick error ->', eventType, error);
      }
    },
    [dispatch, extraParameters, identifierObject]
  );

  const trackSystemError = useCallback(
    (eventType: string, parameters?: any) => {
      const formatParameters = convertKeysToSnakeCase(parameters);
      const parametersWithUtm = {
        ...formatParameters,
        ...extraParameters,
      };
      lastActionRef.current = eventType;
      try {
        analytics.track(eventType, parametersWithUtm, identifierObject);
        logger.log('Analytics trackSystemError ->', eventType, parametersWithUtm);
      } catch (error) {
        logger.log('Analytics trackSystemError error ->', eventType, parametersWithUtm, error);
      }
      dispatch({
        type: 'TRACK_SYSTEM_ERROR',
        payload: { eventType, parameters: parametersWithUtm },
      });
    },
    [dispatch, extraParameters, identifierObject]
  );

  const identifyUser = useCallback(
    (userId: string) => {
        try {
          analytics.identify(userId);
          logger.log('Analytics identifyUser ->', userId);
        } catch (error) {
          logger.log('Analytics identifyUser error ->', error);
        }
        dispatch({
          type: 'IDENTIFY_USER',
          payload: { eventType: 'identify_user', parameters: {userId } },
        });
    },
    [dispatch, getTimeSpentMs]
  );

  // Reset tracking for new form instance
  const resetTracking = useCallback(() => {
    interactionStartedRef.current = false;
    interactionStartTimeRef.current = null;
    abandonedRef.current = false;
    pageExitTrackedRef.current = false;
    setIsIdle(false);
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
  }, []);

  const trackFormSubmit = useCallback(
    (eventType: string, parameters?: any, isReset = false) => {
      // Mark as submitted to prevent abandon event
      abandonedRef.current = true;
      const formatParameters = convertKeysToSnakeCase(parameters);
      const parametersWithUtm = {
        ...formatParameters,
        ...extraParameters,
      };
      try {
        analytics.track(eventType, parametersWithUtm, identifierObject);
        logger.log('Analytics trackFormSubmit ->', eventType, parametersWithUtm);
      } catch (error) {
        logger.log('Analytics trackFormSubmit error ->', eventType, parametersWithUtm, error);
      }
      isReset && resetTracking();
    },
    [extraParameters, resetTracking]
  );

  return {
    startInteraction,
    trackPage,
    trackClick,
    trackFormSubmit,
    trackSystemError,
    identifyUser,
    resetTracking,
    isIdle,
  };
};
